import React, { forwardRef, RefObject } from 'react';
import { Box, Grid } from '@mui/material';
import { isNil } from 'ramda';
import { IBuilding } from 'spw-redux/modules/projects/types';
import { isTotalProgram } from 'spw-components/programming-scenarios/building-hs-sc-scenario/utils';
import { commaSeparated, numberWithCommasOrDash } from 'spw-shared/utils';
import { isUSFCalculator } from 'spw-components/growth-calculator/utils';
import { isNaN } from 'lodash';
import { NO_DATA_TO_SHOW } from 'spw-constants';
import { CardValueTitle } from 'spw-programming-projects/program-summary/components/styles';
import KeyDataPointsDialog from 'spw-components/dialogs/total-program/key-data-points-dialog';
import { KEY_DATA_POINT_VALUES } from 'spw-components/dialogs/total-program/utils';
import { useDialogStatus } from 'spw-components/dialogs/total-program/use-dialog-status';
import { IProjectSettings } from 'spw-redux/modules/settings/types';
import { isDepartmentScenario } from 'spw-components/worksheet/utils';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import {
  CardTitle,
  KeyDataPointsCard,
  CardValue,
  DeltaCardValue,
  KeyDataPointColumn,
  KeyDataPointColumnContainer,
  DepartmentValue,
  SecondaryIdentifierValue,
} from './styles';
import { PlainProgramScenario } from '../../../../models/Scenario';
import { SummaryCalculator } from '../../../../models/ProgramSummaryCalculator';
import { PlainWorksheet } from '../../../../models/Worksheet';

const getClickHandler = (type: 'USF' | 'RSF', clickHandler: () => void) =>
  type === 'USF' ? clickHandler : undefined;

const MidColumn = ({
  totalAvailableUsf,
  totalProgramUsf,
  delta,
  isTotalProgramScenario,
  showTotalAvailableUsf,
  showTotalProgramUsf,
  showDelta,
  testingPrefix,
  type,
}: {
  totalAvailableUsf: number;
  totalProgramUsf: number;
  delta: string;
  isTotalProgramScenario: boolean;
  showTotalAvailableUsf: () => void;
  showTotalProgramUsf: () => void;
  showDelta: () => void;
  testingPrefix: string;
  type: 'USF' | 'RSF';
}) => {
  const { getUnit } = useUnitConversion();
  const numericDelta = parseFloat(delta.replace(/,/g, ''));

  const deltaCardClass = (() => {
    if (numericDelta > 0) return 'positive';
    if (numericDelta < 0) return 'negative';
    return undefined;
  })();

  const areaUnit = getUnit(type === 'RSF' ? 'rentable-area' : 'area');

  return (
    <Box sx={{ flexGrow: 1 }}>
      <KeyDataPointColumnContainer>
        <KeyDataPointColumn
          className={
            isTotalProgramScenario && type === 'USF' ? 'highlight' : ''
          }
          onClick={getClickHandler(type, showTotalAvailableUsf)}
        >
          <CardValue data-testid={`${testingPrefix}_totalAvailable${type}`}>
            {numberWithCommasOrDash(totalAvailableUsf)}
          </CardValue>
          <CardValueTitle
            data-testid={`${testingPrefix}_totalAvailable${type}_label`}
          >
            Total Available {areaUnit}
          </CardValueTitle>
        </KeyDataPointColumn>
        <KeyDataPointColumn
          className={
            isTotalProgramScenario && type === 'USF' ? 'highlight' : ''
          }
          onClick={getClickHandler(type, showTotalProgramUsf)}
        >
          <CardValue data-testid={`${testingPrefix}_totalProgram${type}`}>
            {numberWithCommasOrDash(totalProgramUsf)}
          </CardValue>
          <CardValueTitle
            data-testid={`${testingPrefix}_totalProgram${type}_label`}
          >
            Total Program {areaUnit}
          </CardValueTitle>
        </KeyDataPointColumn>
        <KeyDataPointColumn
          className={`last ${
            isTotalProgramScenario && type === 'USF' ? 'highlight' : ''
          }`}
          onClick={getClickHandler(type, showDelta)}
        >
          <DeltaCardValue
            data-testid={`${testingPrefix}_delta`}
            className={deltaCardClass}
          >
            {delta && delta !== '0' ? delta : NO_DATA_TO_SHOW}
          </DeltaCardValue>
          <CardValueTitle data-testid={`${testingPrefix}_delta_label`}>
            Program Delta {areaUnit}
          </CardValueTitle>
        </KeyDataPointColumn>
      </KeyDataPointColumnContainer>
    </Box>
  );
};

const LastColumn = ({
  futureHeadcount,
  futureWorkSeats,
  futureMeetingSeats,
  isTotalProgramScenario,
  showFutureHeadcount,
  showFutureWorkSeats,
  showFutureMeetingSeats,
  testingPrefix,
}: {
  futureHeadcount: number;
  futureWorkSeats: number;
  futureMeetingSeats: number;
  isTotalProgramScenario: boolean;
  showFutureHeadcount: () => void;
  showFutureWorkSeats: () => void;
  showFutureMeetingSeats: () => void;
  testingPrefix: string;
}) => (
  <Box sx={{ flexGrow: 1 }}>
    <KeyDataPointColumnContainer className="left-border">
      <KeyDataPointColumn
        className={isTotalProgramScenario ? 'highlight' : ''}
        onClick={showFutureHeadcount}
      >
        <CardValue data-testid={`${testingPrefix}_futureHeadcount`}>
          {numberWithCommasOrDash(futureHeadcount)}
        </CardValue>
        <CardValueTitle data-testid={`${testingPrefix}_futureHeadcount_label`}>
          Headcount
        </CardValueTitle>
      </KeyDataPointColumn>
      <KeyDataPointColumn
        className={isTotalProgramScenario ? 'highlight' : ''}
        onClick={showFutureWorkSeats}
      >
        <CardValue data-testid={`${testingPrefix}_futureWorkSeats`}>
          {numberWithCommasOrDash(futureWorkSeats)}
        </CardValue>
        <CardValueTitle data-testid={`${testingPrefix}_futureWorkSeats_label`}>
          Work Seats
        </CardValueTitle>
      </KeyDataPointColumn>
      <KeyDataPointColumn
        className={`last ${isTotalProgramScenario ? 'highlight' : ''}`}
        onClick={showFutureMeetingSeats}
      >
        <CardValue data-testid={`${testingPrefix}_futureMeetingSeats`}>
          {numberWithCommasOrDash(futureMeetingSeats)}
        </CardValue>
        <CardValueTitle
          data-testid={`${testingPrefix}_futureMeetingSeats_label`}
        >
          Meeting Seats
        </CardValueTitle>
      </KeyDataPointColumn>
    </KeyDataPointColumnContainer>
  </Box>
);

const DataColumn = ({
  department,
  growthYear,
  targetYear,
  ratio,
  isTotalProgramScenario,
  departmentSecondaryIdentifier,
  testingPrefix,
}: {
  department: string | number;
  growthYear: number;
  targetYear: number;
  ratio: number;
  isTotalProgramScenario?: boolean;
  departmentSecondaryIdentifier?: string;
  testingPrefix: string;
}) => {
  let growthRatio = 'NA';
  if (!isNil(ratio)) {
    growthRatio = !isNaN(ratio) ? `${ratio?.toFixed(1)}%` : NO_DATA_TO_SHOW;
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <KeyDataPointColumnContainer className="right-border padding-0">
        <KeyDataPointColumn>
          {isTotalProgramScenario ? (
            <CardValue data-testid={`${testingPrefix}_dept`}>
              {department}
            </CardValue>
          ) : (
            <DepartmentValue
              data-testid={`${testingPrefix}_dept`}
              className={
                departmentSecondaryIdentifier ? 'secondary-identifier' : ''
              }
            >
              {department}
            </DepartmentValue>
          )}

          {departmentSecondaryIdentifier && (
            <SecondaryIdentifierValue
              data-testid={`${testingPrefix}_secondaryId`}
            >
              {departmentSecondaryIdentifier}
            </SecondaryIdentifierValue>
          )}
          <CardValueTitle data-testid={`${testingPrefix}_dept_label`}>
            Department{isTotalProgramScenario ? 's Linked' : ''}
          </CardValueTitle>
        </KeyDataPointColumn>
        <KeyDataPointColumn>
          <CardValue data-testid={`${testingPrefix}_targetYear`}>
            {growthYear > 0
              ? `Year ${growthYear} (${targetYear})`
              : `Current (${targetYear})`}
          </CardValue>
          <CardValueTitle data-testid={`${testingPrefix}_targetYear_label`}>
            Target Year
          </CardValueTitle>
        </KeyDataPointColumn>
        <KeyDataPointColumn className="last">
          <CardValue data-testid={`${testingPrefix}_growthRatio`}>
            {growthRatio}
          </CardValue>
          <CardValueTitle data-testid={`${testingPrefix}_growthRatio_label`}>
            {isTotalProgramScenario ? 'Compounded ' : ''}Growth %
          </CardValueTitle>
        </KeyDataPointColumn>
      </KeyDataPointColumnContainer>
    </Box>
  );
};

interface IKeyDataPointsProps {
  showRsf: boolean;
  summaryCalculator: SummaryCalculator;
  scenario: PlainProgramScenario;
  building: IBuilding;
  futureWorkSeats: number;
  futureHeadcount: number;
  futureMeetingSeats: number;
  totalProgramWorksheets?: PlainWorksheet[];
  projectSettings: IProjectSettings;
}

const KeyDataPoints = forwardRef<HTMLElement, IKeyDataPointsProps>(
  (
    {
      showRsf,
      summaryCalculator,
      scenario,
      building,
      futureWorkSeats,
      futureHeadcount,
      futureMeetingSeats,
      totalProgramWorksheets,
      projectSettings,
    },
    ref
  ) => {
    const { convertArea } = useUnitConversion();
    const isTotalProgramScenario = isTotalProgram(scenario);
    const isUsfSummary = isUSFCalculator(scenario.calculatorType);

    const { targetYear, startYear, primaryLabel } = scenario;
    const growthYear = targetYear - startYear;

    const {
      deltaProgramUSF,
      deltaProgramRSF,
      totalProgramUSF,
      totalProgramRSF,
    } = summaryCalculator;
    const deltaProgramUSFString =
      deltaProgramUSF > 0
        ? `+${commaSeparated(Math.round(convertArea(deltaProgramUSF)))}`
        : `${commaSeparated(Math.round(convertArea(deltaProgramUSF)))}`;

    const deltaProgramRSFString =
      deltaProgramRSF > 0
        ? `+${commaSeparated(Math.round(convertArea(deltaProgramRSF)))}`
        : `${commaSeparated(Math.round(convertArea(deltaProgramRSF)))}`;

    const {
      showDialog,
      openDialog,
      valueType,
      dialogValue,
      setShowDialog,
    } = useDialogStatus(isTotalProgramScenario);

    const showTotalAvailableUsf = () => {
      openDialog(
        KEY_DATA_POINT_VALUES.TOTAL_AVAILABLE_USF,
        scenario.totalAvailableUSF
      );
    };
    const showTotalProgramUsf = () => {
      openDialog(
        KEY_DATA_POINT_VALUES.TOTAL_PROGRAM_USF,
        summaryCalculator.totalProgramUSF
      );
    };

    const showDelta = () => {
      openDialog(KEY_DATA_POINT_VALUES.DELTA, deltaProgramUSF);
    };

    const showFutureHeadcount = () => {
      openDialog(KEY_DATA_POINT_VALUES.FUTURE_HEADCOUNT, futureHeadcount);
    };

    const showFutureWorkSeats = () => {
      openDialog(KEY_DATA_POINT_VALUES.FUTURE_WORK_SEATS, futureWorkSeats);
    };

    const showFutureMeetingSeats = () => {
      openDialog(
        KEY_DATA_POINT_VALUES.FUTURE_MEETING_SEATS,
        futureMeetingSeats
      );
    };

    const getDepartmentLabel = () => {
      if (isTotalProgramScenario) return totalProgramWorksheets?.length;
      if (isDepartmentScenario(scenario)) return primaryLabel;
      return 'NA';
    };

    return (
      <div ref={ref as RefObject<HTMLDivElement>}>
        <KeyDataPointsCard>
          <CardTitle
            style={{ paddingLeft: '8px' }}
            data-testid="programming_ps_key_data_title"
          >
            KEY DATA POINTS
          </CardTitle>
          <Grid container>
            {isTotalProgramScenario && showDialog && !window.SPWExport && (
              <KeyDataPointsDialog
                anchorRef={ref}
                totalProgramWorksheets={totalProgramWorksheets}
                showDialog={showDialog}
                setShowDialog={setShowDialog}
                value={dialogValue}
                valueType={valueType}
                showTotalAvailableUsf={showTotalAvailableUsf}
                showTotalProgramUsf={showTotalProgramUsf}
                showDelta={showDelta}
                showFutureHeadcount={showFutureHeadcount}
                showFutureWorkSeats={showFutureWorkSeats}
                showFutureMeetingSeats={showFutureMeetingSeats}
                building={building}
                projectSettings={projectSettings}
              />
            )}

            {!isUsfSummary && (
              <DataColumn
                department={getDepartmentLabel()}
                departmentSecondaryIdentifier={
                  isDepartmentScenario(scenario)
                    ? scenario.secondaryLabel
                    : undefined
                }
                growthYear={growthYear}
                ratio={scenario.growthPercentage}
                targetYear={targetYear}
                isTotalProgramScenario={isTotalProgramScenario}
                testingPrefix="programming_ps_key_data"
              />
            )}

            <MidColumn
              totalAvailableUsf={convertArea(scenario.totalAvailableUSF)}
              totalProgramUsf={convertArea(totalProgramUSF)}
              delta={deltaProgramUSFString}
              isTotalProgramScenario={isTotalProgramScenario}
              showTotalAvailableUsf={showTotalAvailableUsf}
              showTotalProgramUsf={showTotalProgramUsf}
              showDelta={showDelta}
              testingPrefix="programming_ps_key_data"
              type="USF"
            />

            {showRsf && (
              <MidColumn
                totalAvailableUsf={convertArea(scenario.totalAvailableRSF)}
                totalProgramUsf={convertArea(totalProgramRSF)}
                delta={deltaProgramRSFString}
                isTotalProgramScenario={isTotalProgramScenario}
                showTotalAvailableUsf={showTotalAvailableUsf}
                showTotalProgramUsf={showTotalProgramUsf}
                showDelta={showDelta}
                testingPrefix="programming_ps_key_data_rsf"
                type="RSF"
              />
            )}

            <LastColumn
              futureHeadcount={futureHeadcount}
              futureWorkSeats={futureWorkSeats}
              futureMeetingSeats={futureMeetingSeats}
              isTotalProgramScenario={isTotalProgramScenario}
              showFutureHeadcount={showFutureHeadcount}
              showFutureWorkSeats={showFutureWorkSeats}
              showFutureMeetingSeats={showFutureMeetingSeats}
              testingPrefix="programming_ps_key_data"
            />
          </Grid>
        </KeyDataPointsCard>
      </div>
    );
  }
);

const MemoizedKeyDataPoints = React.memo(KeyDataPoints);

export { MemoizedKeyDataPoints as KeyDataPoints };
